import { NCRBaseButtonProps } from 'src/common/components/base-button/base-button';
import {
  buildEmeraldModalProps,
  EmeraldDialogProps,
  showEmeraldDialog,
} from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { IModal } from 'src/common/components/interactive-dialogue/components/interactive-dialogue-container';
import { showEmeraldSelectionDialog } from 'src/common/components/interactive-dialogue/components/selection-dialogue/selection-dialog';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { TenderType } from 'src/common/enumerations/tender-type';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { IRefundTenderGroup, ITender } from 'src/common/interfaces/dtos/ITender';
import { store } from 'src/common/redux/configure-store';
import { TenderingActions } from 'src/common/redux/reducers/tendering-reducer';
import { getPaymentOptions } from 'src/home-page/business-logic/get-payment-options';
import { updateCartStatus } from 'src/home-page/business-logic/update-cart-status';
import {
  getTenderAmountMessage,
  getTriButtonModalBottom,
  TriButtonModalBottomProps,
} from 'src/home-page/components/finish-transaction/tender-refund-message';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import showPinpadDisconnectedModal from '../../common/components/pinpad-disconnected-modal';
import { refundPayment } from './handle-tender-refund';
import { approveRefundSwitchTender } from './services/refund-switch-tender-approval';

const SWITCH_TENDER_MODAL_ID = 'switch-tender-modal';
const EWIC_RETURN_TENDER_MODAL_ID = 'ewic-return-tender-modal';

export const handleSwitchRefundTenders = async (
  refundTenderGroup: IRefundTenderGroup,
): Promise<void> => {
  try {
    await getPaymentOptions(refundTenderGroup.refundableTenders, true, false);
    await approveRefundSwitchTender();
    showSwitchTenderModal(refundTenderGroup);
  } catch (err) {
    store.dispatch(TenderingActions.setIsTendering(false));
    popupMessage(err);
  }
};

export const showRefundModal = (refundTenderGroup: IRefundTenderGroup): void => {
  const refundTender = refundTenderGroup.refundTender;
  const modalMessage = refundTender ? 'HeaderControl_TenderRefundGroupMenuView' : 'TenderOption';

  const modalSubMessage = refundTender?.length
    ? [
        getTenderAmountMessage({
          amount: store.getState().itemList.amountDue,
          refundTender: refundTender,
        }),
      ]
    : 'ConfirmDefaultTender_NoRecommendedTenders';

  const onClose = async () => {
    store.dispatch(TenderingActions.setIsTendering(false));
    popEmeraldModal();
    await updateCartStatus();
  };

  const onConfirm = () => {
    popEmeraldModal();
    if (refundTender) {
      refundPayment(refundTender[0]);
    }
  };

  const onSwitch = () => {
    popEmeraldModal();
    handleSwitchRefundTenders(refundTenderGroup);
  };

  const triButtonModalBottomProps: TriButtonModalBottomProps = {
    closeButton: {
      label: 'ButtonCancelStyle_Cancel',
      onClick: onClose,
    },
    middleButton: {
      label: 'Switch_Tender',
      onClick: onSwitch,
      disabled: !refundTenderGroup.refundableTenders?.length,
    },
    rightButton: {
      label: 'Confirm',
      onClick: onConfirm,
      disabled: !refundTender?.length,
    },
  };

  const refundModal: IMessageModal = {
    message: modalMessage,
    type: EmeraldDialogTypes.InfoDecision,
    subMessage: modalSubMessage,
    buttonComponent: getTriButtonModalBottom(triButtonModalBottomProps),
    disableBackdropClick: true,
  };

  showEmeraldDialog(refundModal);
};

export const showSwitchTenderModal = (refundTenderGroup: IRefundTenderGroup) => {
  const { refundableTenders } = refundTenderGroup;

  if (!refundableTenders) {
    return;
  }
  const onConfirm = (data: string) => {
    const tender = refundableTenders.find(tender => tender.id === data);
    if (tender) {
      // TODO: tenderTypeString - this is a workaround should be removed when we get the un-mocked refund tenders
      const tenderTypeString = tender.type as string;
      const pinpadConnected = store.getState().pinPad.isPinpadConfigured;
      if (
        (tender.type === TenderType.CreditDebit || tenderTypeString === 'EFT') &&
        !pinpadConnected
      ) {
        showPinpadDisconnectedModal();
      } else {
        const refundTender = { ...tender, amount: getRefundDue(tender) };
        refundPayment(refundTender, true);
        popEmeraldModal(SWITCH_TENDER_MODAL_ID);
      }
    }
  };
  const onClose = (): void => {
    store.dispatch(TenderingActions.setIsTendering(false));
    popEmeraldModal(SWITCH_TENDER_MODAL_ID);
    showRefundModal(refundTenderGroup);
  };

  showEmeraldSelectionDialog(
    {
      selectListItems: refundableTenders,
      message: 'TenderingView_SelectTender',
      subMessage: 'TenderingView_SelectTender_Full',
      onCancel: onClose,
      onAccept: onConfirm,
      dataTestId: 'switch-tender-modal',
      disableBackdropClick: true,
    },
    SWITCH_TENDER_MODAL_ID,
  );
};

export const getRefundDue = (tender: ITender): number => {
  const precision =
    store.getState().touchPoint.touchPointConfiguration.defaultAmountDecimalPrecision || 2;
  let refundDue = -1 * store.getState().itemList.amountDue;
  if (tender && tender.isForeignCurrency && tender.exchangeRate) {
    refundDue = Number((refundDue * tender.exchangeRate).toFixed(precision));
  }
  return refundDue;
};

export const returnEwicNotification = (refundTenderGroup: IRefundTenderGroup): void => {
  const declineButton: NCRBaseButtonProps = {
    'data-testid': 'dialog-messages-close',
    buttonType: 'white',
    onClick: async () => {
      store.dispatch(TenderingActions.setIsTendering(false));
      popEmeraldModal(EWIC_RETURN_TENDER_MODAL_ID);
      await updateCartStatus();
    },
    children: 'Cancel',
  };

  const acceptButton: NCRBaseButtonProps = {
    'data-testid': 'dialog-messages-accept',
    buttonType: 'primary',
    onClick: () => {
      popEmeraldModal(EWIC_RETURN_TENDER_MODAL_ID);
      showRefundModal(refundTenderGroup);
    },
    children: 'Continue',
  };

  const dialogProps: EmeraldDialogProps = {
    message: 'RefundNotificationTitle',
    type: EmeraldDialogTypes.Info,
    subMessage: 'RefundEwicNotificationSubTitle',
  };

  const modalProps: IModal = {
    modalId: EWIC_RETURN_TENDER_MODAL_ID,
    open: true,
    rightButtons: [declineButton, acceptButton],
  };
  showEmeraldDialog(buildEmeraldModalProps(dialogProps, modalProps));
};
